import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Bot, Users, Clock, DollarSign, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import JobApplicationModal from '@/components/JobApplicationModal';


const FreelanceAITechnician = () => {
  const { t } = useLanguage();
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <section className="pt-24 pb-20 px-4 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="float absolute top-20 right-32 w-28 h-28 bg-primary/15 rounded-full blur-xl" style={{ animationDelay: '1s' }}></div>
          <div className="float absolute bottom-32 left-32 w-20 h-20 bg-accent/25 rounded-full blur-2xl" style={{ animationDelay: '3s' }}></div>
        </div>

        <div className="container mx-auto max-w-4xl relative z-10">
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" className="mb-8 hover:bg-primary/10">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('jobs.backToHome')}
            </Button>
          </Link>

          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent mb-4">
              {t('freelanceAITechnician.title')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('freelanceAITechnician.heroDescription')}
            </p>
            <Button 
              size="lg" 
              className="mt-6 bg-gradient-to-r from-primary to-primary/90 hover:shadow-lg hover:shadow-primary/25 transition-all duration-300"
              onClick={() => setIsApplicationOpen(true)}
            >
              {t('jobs.applyNow')}
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Job Description */}
            <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" />
                  {t('jobs.jobDescription')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('freelanceAITechnician.jobDescriptionText')}
                </p>
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('jobs.keyResponsibilities')}</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• {t('freelanceAITechnician.responsibility1')}</li>
                    <li>• {t('freelanceAITechnician.responsibility2')}</li>
                    <li>• {t('freelanceAITechnician.responsibility3')}</li>
                    <li>• {t('freelanceAITechnician.responsibility4')}</li>
                    <li>• {t('freelanceAITechnician.responsibility5')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Qualifications */}
            <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  {t('jobs.requiredQualifications')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold mb-2">{t('jobs.technicalSkills')}</h4>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">Python</Badge>
                      <Badge variant="secondary">Machine Learning</Badge>
                      <Badge variant="secondary">TensorFlow</Badge>
                      <Badge variant="secondary">PyTorch</Badge>
                      <Badge variant="secondary">OpenAI API</Badge>
                      <Badge variant="secondary">LangChain</Badge>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">{t('jobs.experience')}</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• {t('freelanceAITechnician.experience1')}</li>
                      <li>• {t('freelanceAITechnician.experience2')}</li>
                      <li>• {t('freelanceAITechnician.experience3')}</li>
                      <li>• {t('freelanceAITechnician.experience4')}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* What We Offer */}
          <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-sm border-border/50 mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                {t('jobs.whatWeOffer')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-3">
                    <DollarSign className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">{t('freelanceAITechnician.premiumRates')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('freelanceAITechnician.premiumRatesDescription')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-3">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">{t('freelanceAITechnician.flexibleWork')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('freelanceAITechnician.flexibleWorkDescription')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-3">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">{t('freelanceAITechnician.expertNetwork')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('freelanceAITechnician.expertNetworkDescription')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-3">
                    <Bot className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">{t('freelanceAITechnician.cuttingEdgeAI')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('freelanceAITechnician.cuttingEdgeAIDescription')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Apply Section */}
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">{t('freelanceAITechnician.readyToShape')}</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {t('freelanceAITechnician.readyToShapeDescription')}
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-primary to-primary/90 hover:shadow-lg hover:shadow-primary/25 transition-all duration-300 px-8"
              onClick={() => setIsApplicationOpen(true)}
            >
              {t('jobs.applyForPosition')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      <JobApplicationModal 
        open={isApplicationOpen}
        onOpenChange={setIsApplicationOpen}
        positionType="Freelance AI Technician"
      />
    </div>
  );
};

export default FreelanceAITechnician;